// TermInput.tsx
import React, { useState } from "react";

import { formatTerm, parseTerm } from "../../../utils/proposalDocuments/term";

interface TermInputProps {
  id: string;
  value: string; // e.g. "10D", "12M", "3Y"
  onChange: (term: string) => void;
}

export type TermUnit = "D" | "M" | "Y";

const TermInput: React.FC<TermInputProps> = ({ id, value, onChange }) => {
  const parsed = parseTerm(value);
  const [amount, setAmount] = useState(parsed.amount);
  const [unit, setUnit] = useState<TermUnit>(parsed.unit);
  const [prevValue, setPrevValue] = useState(value);

  // Keep local state in sync if the term is changed externally
  // (e.g. loading a saved section) - adjusted during render rather than in an effect
  if (value !== prevValue) {
    setPrevValue(value);
    setAmount(parsed.amount);
    setUnit(parsed.unit);
  }

  const emit = (newAmount: string, newUnit: TermUnit) => {
    if (newAmount === "") {
      onChange("");
      return;
    }
    onChange(formatTerm(newAmount, newUnit));
  };

  const handleAmountChange = (v: string) => {
    setAmount(v);
    emit(v, unit);
  };

  const handleUnitChange = (v: TermUnit) => {
    setUnit(v);
    emit(amount, v);
  };

  return (
    <div className="term-input">
      <input
        id={id}
        type="number"
        min={1}
        className={"term-input__amount"}
        placeholder="e.g. 10"
        value={amount}
        onChange={(e) => {
          handleAmountChange(e.target.value);
        }}
        aria-label="Term amount"
      />
      <select
        className="term-input__unit"
        value={unit}
        onChange={(e) => {
          handleUnitChange(e.target.value as TermUnit);
        }}
        aria-label="Term unit"
      >
        <option value="D">Days</option>
        <option value="M">Months</option>
        <option value="Y">Years</option>
      </select>
    </div>
  );
};

export default TermInput;
